import {
    useFirestoreGeneral,
    useFirestoreNoCompagny,
} from '../../firebase/useFirestore';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { getViewerUrl, getEditorUrl } from '../../components/liveReport/reportBuilder2/utils/editorUrl';
import { copyReportName } from '../../components/liveReport/reportBuilder2/utils/buildReportCopy';
import { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LiveReportCrudContext } from '../../components/liveReport/reportBuilder2/context/LiveReportCrudContext';
import ShareReportModal from '../../components/liveReport/reportBuilder2/components/ShareReportModal';
import ReportPersonaSelector from '../../components/liveReport/reportBuilder2/components/ReportPersonaSelector';
import ReportPersonaButton from '../../components/liveReport/reportBuilder2/components/ReportPersonaButton';
import NewReportModal from '../../components/liveReport/reportBuilder2/components/NewReportModal';
import useSettings from '../../hooks/Settings';
import useSharedCollection from '../../firebase/useSharedCollection';
import DeleteModal from '../../components/common/DeleteModal';
import Modal from '../../components/common/Modal';
import PeriodSelector from '../../components/common/PeriodSelector';
import formatReportPeriod from '../../helpers/liveReportHelpers/formatReportPeriod';
import Table from '../../components/common/table/Table.jsx';
import TableCell from '../../components/common/table/TableCell.jsx';
import { client } from '../../hooks/Client';
import { isSuperAdminTenant } from '../../utils/superAdminTenant';
import describeReportsList from '../../helpers/liveReportHelpers/describeReportsList';
import isTemplateAvailable from '../../helpers/liveReportHelpers/isTemplateAvailable';

/**
 * @typedef {import('../../types/LiveReports2').LiveReports2Doc} LiveReports2Doc
 */

const LiveReports2 = () => {

    const liveReportCrudContext = useContext(LiveReportCrudContext);
    const history = useHistory();

    if (!liveReportCrudContext) {
        throw new Error('LiveReportCrudContext not found.');
    }

    const {
        createNewReport,
        createNewReportFromSource,
        deleteReport,
    } = liveReportCrudContext;

    const [search, setSearch] = useState('');

    // The report being copied right now — one at a time, so a double click
    // can't make two copies.
    const [copyingReportId, setCopyingReportId] = useState(/** @type {string|null} */ (null));
    const [copyError, setCopyError] = useState(/** @type {string|null} */ (null));

    // AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
    // Store the ID (not the doc) so the modal always re-derives from the live
    // `liveReports` list — the doc mutates via the share-code Cloud Function.
    const [shareReportId, setShareReportId] = useState(/** @type {string|null} */ (null));

    // Same ID-not-doc reasoning as shareReportId — the confirm modal re-derives
    // the report from the live list so a stale snapshot can't be deleted.
    const [deleteReportId, setDeleteReportId] = useState(/** @type {string|null} */ (null));

    // Same ID-not-doc reasoning — PeriodSelector writes Start/End straight to
    // Firestore, so the modal must re-derive the report from the live list to
    // reflect the saved period immediately.
    const [periodReportId, setPeriodReportId] = useState(/** @type {string|null} */ (null));

    // Same ID-not-doc reasoning — the selector writes PersonaID (and maybe a
    // ShareCode) and must see the saved values from the live list.
    const [personaReportId, setPersonaReportId] = useState(/** @type {string|null} */ (null));

    /* De plusknop vraagt eerst de titel (B4); pas 'Aanmaken' maakt het rapport. */
    const [newReportOpen, setNewReportOpen] = useState(false);

    // A report about one participant only exists with the Deelnemers module.
    // No module = no column and no Personas listener ('none' while settings load).
    const { personasOn, housevisits, impactFunctionality } = useSettings();
    const personasEnabled = personasOn === 'flex';
    /** @type {Array<{ID: string, Name: string, Status?: string}> | undefined} */
    const personas = useSharedCollection(personasEnabled ? 'Personas' : null);

    /** @type {import('../../types/LiveReports2').LiveReports2Doc[]} */
    // @ts-expect-error useFirestoreNoCompagny() lacks a template arg
    const liveReportTemplatesRaw = useFirestoreNoCompagny('LiveReports2', 'IsTemplate', true);
    // Een deelnemertemplate (RequiredModules) alleen voor wie de module heeft;
    // Deccos ziet ze allemaal om ze te beheren (docs/features/livereport-templates-deelnemer.md, T5).
    const liveReportTemplates = useMemo(() => {
        if (!liveReportTemplatesRaw) return liveReportTemplatesRaw;
        const flags = { personasOn, housevisits, impactFunctionality };
        const showAll = isSuperAdminTenant(client);
        return liveReportTemplatesRaw
            .filter(t => !t.Deleted)
            .filter(t => isTemplateAvailable(t, flags, { showAll }));
    }, [liveReportTemplatesRaw, personasOn, housevisits, impactFunctionality]);

    /** @type {import('../../types/LiveReports2').LiveReports2Doc[]} */
    // @ts-expect-error useFirestoreGeneral() lacks a template arg
    const liveReports = useFirestoreGeneral('LiveReports2', 'IsTemplate', false);

    const createNewReportHandler = async (isTemplate = false) => {
        const newReport = await createNewReport(isTemplate);
        history.push(getEditorUrl(newReport.ID, isTemplate));
    };

    /**
     * Copy a report as a new draft of this organisation. The copy shows up in
     * the list through the live listener, so the user stays on this page.
     * @param {LiveReports2Doc} report
     */
    const copyReportHandler = async (report) => {
        if (copyingReportId) return;
        setCopyingReportId(report.ID);
        setCopyError(null);
        try {
            await createNewReportFromSource(report, false, copyReportName(report));
        } catch (error) {
            console.error('Error copying report:', error);
            setCopyError(`${report.Name} kon niet gekopieerd worden. Probeer het opnieuw.`);
        } finally {
            setCopyingReportId(null);
        }
    };

    const viewTemplateHandler = (templateId) => {
        history.push(getViewerUrl(templateId, true));
    };

    /* Counter and empty message both come out of one function, so the page can
       never claim '0 rapportages' while the read is still in flight.
       AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */
    const reportsList = useMemo(
        () => describeReportsList(liveReports, search),
        [liveReports, search]
    );
    const filteredReports = reportsList.items;

    const reportColumns = [
        { key: 'title', label: 'Titel' },
        { key: 'period', label: 'Periode', searchable: false },
        ...(personasEnabled ? [{ key: 'persona', label: 'Deelnemer', searchable: false }] : []),
        { key: 'status', label: 'Status', searchable: false },
        { key: 'access', label: 'Toegang', searchable: false },
        { key: 'copy', label: 'Kopiëren', searchable: false, headerClassName: 'reports-col-action-head' },
        { key: 'share', label: 'Delen', searchable: false, headerClassName: 'reports-col-action-head' },
        { key: 'delete', label: 'Verwijderen', searchable: false, headerClassName: 'reports-col-action-head' },
    ];

    return (
        <div className="main">
            <div className="main-container">

                <div className="reports-page-header">
                    <div>
                        <h1>Rapportages</h1>
                        <p>Al je rapportages en templates op één plek.</p>
                    </div>
                </div>

                <section className="reports-card">
                    <div className="reports-card-header">
                        <div className="reports-card-title">
                            <h2>Mijn rapportages</h2>
                            <p>{reportsList.countLabel}</p>
                        </div>
                        <div className="reports-search">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Zoek rapportages..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {copyError && (
                        <p className="reports-copy-error" role="alert">{copyError}</p>
                    )}

                    <Table
                        columns={reportColumns}
                        items={filteredReports}
                        getItemKey={(report) => report.ID}
                        showSearch={false}
                        tableClassName="reports-table-common"
                        containerClassName="reports-table-container"
                        outerClassName="reports-table-outer"
                        getRowClassName={() => 'reports-table-row-clickable'}
                        rowProps={(report) => ({
                            onClick: () => { history.push(getViewerUrl(report.ID)); },
                        })}
                        emptyMessage={reportsList.emptyMessage}
                        addRow={{
                            toolTipContent: 'Nieuwe rapportage',
                            onClick: () => setNewReportOpen(true),
                        }}
                        renderRow={(report) => (
                            <>
                                <TableCell className="reports-col-title">
                                    <div className="reports-row-title-wrapper">
                                        <div className="reports-row-thumb">
                                            {report.Thumbnail
                                                ? <img src={report.Thumbnail} alt="" className="reports-row-thumb-image" />
                                                : <DescriptionOutlinedIcon />}
                                        </div>
                                        <div className="reports-row-title-text">
                                            <div className="reports-row-name">{report.Name}</div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="reports-col-period">
                                    <button
                                        type="button"
                                        className="reports-period-button"
                                        title="Periode instellen"
                                        aria-label="Periode instellen"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setPeriodReportId(report.ID);
                                        }}
                                    >
                                        <CalendarTodayOutlinedIcon />
                                        <span>{formatReportPeriod(report)}</span>
                                    </button>
                                </TableCell>
                                {personasEnabled && (
                                    <TableCell className="reports-col-persona">
                                        <ReportPersonaButton
                                            report={report}
                                            personas={personas}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setPersonaReportId(report.ID);
                                            }}
                                        />
                                    </TableCell>
                                )}
                                <TableCell className="reports-col-status">
                                    <span className={`reports-status-badge reports-status-badge--${report.Status === 'published' ? 'published' : 'draft'}`}>
                                        {report.Status === 'published' ? 'Gepubliceerd' : 'Concept'}
                                    </span>
                                </TableCell>
                                <TableCell className="reports-col-access">
                                    <span className={`reports-access-badge reports-access-badge--${report.ShareCode ? 'secure' : 'public'}`}>
                                        {report.ShareCode ? 'Beveiligd' : 'Publiek'}
                                    </span>
                                </TableCell>
                                <TableCell className="reports-col-action">
                                    <button
                                        type="button"
                                        className="reports-action-button"
                                        title="Rapport kopiëren"
                                        aria-label="Rapport kopiëren"
                                        disabled={copyingReportId !== null}
                                        onClick={e => {
                                            e.stopPropagation();
                                            copyReportHandler(report);
                                        }}
                                    >
                                        <ContentCopyOutlinedIcon />
                                    </button>
                                </TableCell>
                                <TableCell className="reports-col-action">
                                    {report.Status === 'published' && (
                                        <button
                                            type="button"
                                            className="reports-action-button"
                                            title="Beveiligd delen"
                                            aria-label="Beveiligd delen"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setShareReportId(report.ID);
                                            }}
                                        >
                                            <ShareOutlinedIcon />
                                        </button>
                                    )}
                                </TableCell>
                                <TableCell className="reports-col-action">
                                    <button
                                        type="button"
                                        className="reports-action-button reports-action-button--danger"
                                        title="Verwijderen"
                                        aria-label="Verwijderen"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setDeleteReportId(report.ID);
                                        }}
                                    >
                                        <DeleteOutlineOutlinedIcon />
                                    </button>
                                </TableCell>
                            </>
                        )}
                    />
                </section>

                <section className="reports-card">
                    <div className="reports-card-header">
                        <div className="reports-card-title">
                            <h2>Templates</h2>
                            <p>Start een nieuwe rapportage vanuit een vooraf opgemaakt template.</p>
                        </div>
                    </div>

                    <div className="reports-template-grid">
                        {!Array.isArray(liveReportTemplates) && (
                            <p className="reports-templates-loading">Templates worden opgehaald…</p>
                        )}
                        {liveReportTemplates && liveReportTemplates.map(template => (
                            <TemplateCard
                                key={template.ID}
                                template={template}
                                onView={() => viewTemplateHandler(template.ID)}
                            />
                        ))}
                        {isSuperAdminTenant(client) && (
                            <AddTemplateCard onClick={() => createNewReportHandler(true)} />
                        )}
                    </div>
                </section>
            </div>

            {(() => {
                const sharedReport = shareReportId && liveReports?.find?.(r => r.ID === shareReportId);
                if (!sharedReport) return null;
                return (
                    <ShareReportModal
                        modalOpen={true}
                        setModalOpen={open => { if (!open) setShareReportId(null); }}
                        report={sharedReport}
                    />
                );
            })()}

            {(() => {
                const reportToDelete = deleteReportId && liveReports?.find?.(r => r.ID === deleteReportId);
                if (!reportToDelete) return null;
                return (
                    <DeleteModal
                        deleteModal={true}
                        setDeleteModal={open => { if (!open) setDeleteReportId(null); }}
                        deleteItem={() => deleteReport(reportToDelete)}
                        deleteName={reportToDelete.Name}
                        linkedNote="De widgets van deze rapportage gaan mee, en komen mee terug als je de rapportage terugzet."
                        softDelete
                    />
                );
            })()}

            {(() => {
                const personaReport = personasEnabled && personaReportId && liveReports?.find?.(r => r.ID === personaReportId);
                if (!personaReport) return null;
                return (
                    <ReportPersonaSelector
                        modalOpen={true}
                        setModalOpen={open => { if (!open) setPersonaReportId(null); }}
                        report={personaReport}
                        personas={personas}
                    />
                );
            })()}

            {newReportOpen && (
                <NewReportModal
                    setModalOpen={setNewReportOpen}
                    onCreate={async (name) => {
                        const newReport = await createNewReport(false, name);
                        history.push(getEditorUrl(newReport.ID, false));
                    }}
                />
            )}

            {(() => {
                const periodReport = periodReportId && liveReports?.find?.(r => r.ID === periodReportId);
                if (!periodReport) return null;
                return (
                    <Modal
                        modalOpen={true}
                        setModalOpen={open => { if (!open) setPeriodReportId(null); }}
                        pageHeader="Periode"
                        description="Bepaal welke periode de data in deze rapportage toont. Widgets kunnen hier per widget van afwijken."
                        /* PeriodSelector saves straight to Firestore on change;
                           Opslaan only closes the modal (familiar footer). */
                        confirm={() => setPeriodReportId(null)}
                        cancelLabel="Sluiten"
                        content={
                            <PeriodSelector
                                page="livereport2"
                                docid={periodReport.docid}
                                currentStart={periodReport.Start}
                                currentEnd={periodReport.End}
                            />
                        }
                    />
                );
            })()}
        </div>
    );
};

/**
 * @param {object} props
 * @param {import('../../types/LiveReports2').LiveReports2Doc} props.template
 * @param {() => void} props.onView
 */
const TemplateCard = ({ template, onView }) => {
    return (
        <div className="reports-template-card">
            <div className="reports-template-icon">
                {template.Thumbnail
                    ? <img src={template.Thumbnail} alt="" className="reports-template-icon-image" />
                    : <DescriptionOutlinedIcon />}
            </div>
            <div className="reports-template-title">{template.Name}</div>
            <button
                type="button"
                className="reports-secondary-button"
                onClick={onView}
            >
                Bekijk template
            </button>
        </div>
    );
};

/**
 * @param {object} props
 * @param {() => void} props.onClick
 */
const AddTemplateCard = ({ onClick }) => {
    return (
        <button
            type="button"
            className="reports-template-card reports-template-card--add"
            onClick={onClick}
        >
            <div className="reports-template-icon reports-template-icon--add">
                <AddIcon />
            </div>
            <div className="reports-template-add-title">Nieuwe template</div>
            <div className="reports-template-add-hint">Begin met een leeg canvas</div>
        </button>
    );
};

export default LiveReports2;
